import { ref } from 'vue'

export interface ToastMessage {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  persistent?: boolean
}

const toasts = ref<ToastMessage[]>([])
let toastIdCounter = 0

export const useToast = () => {
  const addToast = (toast: Omit<ToastMessage, 'id'>): string => {
    const id = `toast-${++toastIdCounter}`
    const newToast: ToastMessage = {
      id,
      duration: 5000,
      persistent: false,
      ...toast
    }

    toasts.value.push(newToast)

    // Auto remove if not persistent
    if (!newToast.persistent && newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }

    return id
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAllToasts = () => {
    toasts.value.length = 0
  }

  // Convenience methods
  const success = (title: string, message?: string, options?: Partial<ToastMessage>) => {
    return addToast({
      type: 'success',
      title,
      message,
      ...options
    })
  }

  const error = (title: string, message?: string, options?: Partial<ToastMessage>) => {
    return addToast({
      type: 'error',
      title,
      message,
      duration: 8000, // Longer duration for errors
      ...options
    })
  }

  const warning = (title: string, message?: string, options?: Partial<ToastMessage>) => {
    return addToast({
      type: 'warning',
      title,
      message,
      duration: 6000,
      ...options
    })
  }

  const info = (title: string, message?: string, options?: Partial<ToastMessage>) => {
    return addToast({
      type: 'info',
      title,
      message,
      ...options
    })
  }

  // Special methods for common use cases
  const apiSuccess = (message: string = 'Operação realizada com sucesso') => {
    return success('Sucesso', message, { duration: 3000 })
  }

  const apiError = (err: any, fallbackMessage: string = 'Erro inesperado') => {
    let title = 'Erro'
    let message = fallbackMessage

    // Handle different error formats
    if (err?.response?.data) {
      const data = err.response.data
      
      // Formato 1: { message: "..." }
      if (data.message) {
        message = data.message
      }
      // Formato 2: { error: { message: "..." } }
      else if (data.error?.message) {
        message = data.error.message
      }
      // Formato 3: { title: "...", detail: "..." }
      else if (data.title) {
        title = data.title
        message = data.detail || data.title
      }
    } else if (err?.message) {
      message = err.message
    }

    return error(title, message, {
      duration: 8000
    })
  }

  return {
    // State
    toasts,

    // Core methods
    addToast,
    removeToast,
    clearAllToasts,

    // Convenience methods
    success,
    error,
    warning,
    info,

    // Specialized methods
    apiSuccess,
    apiError
  }
}

// Global toast instance
let globalToastInstance: ReturnType<typeof useToast> | null = null

export const setupGlobalToast = () => {
  globalToastInstance = useToast()
  return globalToastInstance
}

export const getGlobalToast = () => {
  if (!globalToastInstance) {
    globalToastInstance = setupGlobalToast()
  }
  return globalToastInstance
}
